import ReactMarkdown from "react-markdown";
import ReportActionBlock from "./ReportActionBlock";
import MotorInfoBlock from "./MotorInfoBlock";

interface ReportParams {
  type: string;
  deviceIds: string[];
  startDate: string;
  endDate: string;
  format: "excel" | "pdf";
}

interface MotorInfoParams {
  deviceName?: string;
  timestamp?: string;
  data?: any;
  devices?: Array<{ id: string; name: string }>;
}

interface ChatMessageContentProps {
  content: string;
}

const markdownComponents = {
  h1: ({ node, ...props }: any) => (
    <h1 className="text-base font-bold mt-3 mb-2 text-content" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2 className="text-[15px] font-bold mt-2.5 mb-1.5 text-content" {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 className="text-sm font-bold mt-2 mb-1 text-content" {...props} />
  ),
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold text-content" {...props} />
  ),
  em: ({ node, ...props }: any) => (
    <em className="italic text-content-secondary" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc list-inside ml-2 my-1.5 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="list-decimal list-inside ml-2 my-1.5 space-y-0.5" {...props} />
  ),
  li: ({ node, ...props }: any) => (
    <li className="text-content" {...props} />
  ),
  code: ({ node, inline, ...props }: any) =>
    inline ? (
      <code className="bg-app/50 px-1.5 py-0.5 rounded text-[12px] font-mono text-content-secondary" {...props} />
    ) : (
      <code className="block bg-app/50 px-3 py-2 rounded my-2 text-[12px] font-mono text-content-secondary overflow-x-auto" {...props} />
    ),
  blockquote: ({ node, ...props }: any) => (
    <blockquote className="border-l-2 border-primary-500/40 pl-3 my-2 italic text-content-secondary" {...props} />
  ),
  hr: ({ node, ...props }: any) => (
    <hr className="my-3 border-t border-line" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p className="my-1 text-content" {...props} />
  ),
};

export default function ChatMessageContent({ content }: ChatMessageContentProps) {
  // Remove [SUGGESTIONS]...[/SUGGESTIONS] blocks from display
  let displayContent = content.replace(/\[SUGGESTIONS\]([\s\S]*?)\[\/SUGGESTIONS\]/g, "");

  // Extract [REPORT_ACTION]...[/REPORT_ACTION] blocks
  const reportActionRegex = /\[REPORT_ACTION\]([\s\S]*?)\[\/REPORT_ACTION\]/g;
  const reportBlocks: Array<{ start: number; end: number; params: ReportParams }> = [];
  let match;

  while ((match = reportActionRegex.exec(displayContent)) !== null) {
    try {
      // Extract JSON - handle both compact and formatted JSON
      const jsonStr = match[1]
        .trim()
        .replace(/^\{/, "{")
        .replace(/\}$/, "}");

      const params = JSON.parse(jsonStr);

      // Validate required fields
      if (
        params.type &&
        params.deviceIds &&
        Array.isArray(params.deviceIds) &&
        params.startDate &&
        params.endDate &&
        params.format
      ) {
        reportBlocks.push({
          start: match.index,
          end: match.index + match[0].length,
          params,
        });
      }
    } catch (e) {
      console.error("Failed to parse report action:", e, "Content:", match[1]);
    }
  }

  // Extract [MOTOR_INFO]...[/MOTOR_INFO] blocks
  const motorInfoRegex = /\[MOTOR_INFO\]([\s\S]*?)\[\/MOTOR_INFO\]/g;
  const motorInfoBlocks: Array<{ start: number; end: number; params: MotorInfoParams }> = [];

  while ((match = motorInfoRegex.exec(displayContent)) !== null) {
    try {
      const jsonStr = match[1]
        .trim()
        .replace(/^\{/, "{")
        .replace(/\}$/, "}");

      const params = JSON.parse(jsonStr);
      motorInfoBlocks.push({
        start: match.index,
        end: match.index + match[0].length,
        params,
      });
    } catch (e) {
      console.error("Failed to parse motor info:", e, "Content:", match[1]);
    }
  }

  // If no action blocks, render with markdown
  if (reportBlocks.length === 0 && motorInfoBlocks.length === 0) {
    return (
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown components={markdownComponents}>{displayContent}</ReactMarkdown>
      </div>
    );
  }

  // Combine all blocks and sort by position
  const allBlocks: Array<{
    type: "report" | "motorInfo";
    start: number;
    end: number;
    data: any;
  }> = [
    ...reportBlocks.map((b) => ({
      type: "report" as const,
      start: b.start,
      end: b.end,
      data: b.params,
    })),
    ...motorInfoBlocks.map((b) => ({
      type: "motorInfo" as const,
      start: b.start,
      end: b.end,
      data: b.params,
    })),
  ];

  allBlocks.sort((a, b) => a.start - b.start);

  // Split content and insert blocks
  const parts: React.ReactNode[] = [];
  let lastEnd = 0;

  allBlocks.forEach((block, index) => {
    // Add text before this block
    if (block.start > lastEnd) {
      const text = displayContent.slice(lastEnd, block.start).trim();
      if (text) {
        parts.push(
          <div key={`text-${index}`} className="mb-3">
            <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
          </div>
        );
      }
    }

    // Add appropriate block type
    if (block.type === "report") {
      parts.push(
        <ReportActionBlock key={`report-${index}`} params={block.data} />
      );
    } else if (block.type === "motorInfo") {
      // Si hay datos de motor completos, pasar el motorData; si no, pasar dispositivos disponibles
      const motorData = block.data.deviceName && block.data.data
        ? {
            deviceName: block.data.deviceName,
            timestamp: block.data.timestamp || new Date().toISOString(),
            data: block.data.data,
          }
        : undefined;

      parts.push(
        <MotorInfoBlock
          key={`motor-${index}`}
          motorData={motorData}
        />
      );
    }

    lastEnd = block.end;
  });

  // Add any remaining text
  if (lastEnd < displayContent.length) {
    const text = displayContent.slice(lastEnd).trim();
    if (text) {
      parts.push(
        <div key="text-end" className="mt-3">
          <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
        </div>
      );
    }
  }

  return <>{parts}</>;
}
